/**
 * Reusable utility for client-side image compression and resizing.
 * Helps prevent memory issues and payload limits on mobile devices.
 */

interface CompressionOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    mimeType?: string;
}

interface CompressionResult {
    base64: string;
    previewUrl: string;
    blob: Blob;
    file: File;
}

/**
 * Compresses an image file using HTML5 Canvas.
 * @param source The original image file or blob
 * @param options Compression settings
 * @returns Promise with base64, previewUrl, and compressed Blob/File
 */
export const compressImage = (
    source: File | Blob,
    options: CompressionOptions = {}
): Promise<CompressionResult> => {
    const {
        maxWidth = 1280,
        maxHeight = 1280,
        quality = 0.65,
        mimeType = 'image/jpeg'
    } = options;

    const fileName = (source as File).name || 'image.jpg';

    return new Promise((resolve, reject) => {
        const img = new Image();
        const originalUrl = URL.createObjectURL(source);

        // Safety timeout — Android WebView can silently hang on large/HEIC images.
        const timeoutId = window.setTimeout(() => {
            img.onload = null;
            img.onerror = null;
            URL.revokeObjectURL(originalUrl);
            reject(new Error('Image load timeout'));
        }, 15000);

        img.src = originalUrl;

        img.onload = () => {
            window.clearTimeout(timeoutId);
            // Create canvas
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions
            if (width > height) {
                if (width > maxWidth) {
                    height *= maxWidth / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width *= maxHeight / height;
                    height = maxHeight;
                }
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(originalUrl);
                reject(new Error('Failed to get canvas context'));
                return;
            }

            // Draw and compress
            ctx.drawImage(img, 0, 0, width, height);

            img.onload = null;
            img.onerror = null;
            URL.revokeObjectURL(originalUrl);

            canvas.toBlob((blob) => {
                canvas.width = 0;
                canvas.height = 0;

                if (!blob) {
                    reject(new Error('Failed to create blob'));
                    return;
                }

                const compressedFile = new File([blob], fileName, {
                    type: mimeType,
                    lastModified: Date.now(),
                });

                const previewUrl = URL.createObjectURL(blob);

                const reader = new FileReader();
                reader.onload = () => {
                    const dataUrl = reader.result as string;
                    const base64 = dataUrl.split(',')[1] ?? '';
                    resolve({
                        base64,
                        previewUrl,
                        blob,
                        file: compressedFile
                    });
                };
                reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
                reader.readAsDataURL(blob);
            }, mimeType, quality);
        };

        img.onerror = (err) => {
            window.clearTimeout(timeoutId);
            URL.revokeObjectURL(originalUrl);
            reject(err instanceof Error ? err : new Error('Failed to load image'));
        };
    });
};

/**
 * Compress with safe fallback to the original file.
 * On Android WebView, large/HEIC photos can fail to decode in <canvas>;
 * in that case we still want the upload to succeed using the raw file.
 */
export const compressImageSafe = async (
    source: File,
    options: CompressionOptions = {}
): Promise<{ file: File; previewUrl: string; compressed: boolean }> => {
    try {
        const { file, previewUrl } = await compressImage(source, options);
        return { file, previewUrl, compressed: true };
    } catch (err) {
        console.warn('[compressImageSafe] compression failed, using original file', err);
        const previewUrl = URL.createObjectURL(source);
        return { file: source, previewUrl, compressed: false };
    }
};

